import { useState, useEffect, useCallback, useMemo } from "react";
import { Select, Input, Tag, DatePicker, Checkbox, message } from "antd";
import { DataGrid, GridOverlay } from "@mui/x-data-grid";
import { InboxOutlined } from "@ant-design/icons";
import { Area } from "@ant-design/plots";
import moment from "moment";

import { ORG_ID, DOMAIN_ID } from "../../constants";
import { logOutUser } from "../../common";
import { useDebounce } from "../../common/debounce";
import { AN_COLORS, AN_FONT, AN_MONO, anAlpha } from "../../design-system/tokens";
import { useAnTheme } from "../../theme/AnThemeProvider";
import { useScrollbarWhileScrolling } from "../../design-system/tableUi";
import { dispatchGetAuthLog } from "../../utils/apiservice";
import { decisionMeta, bucketTimeline } from "../../utils/authLog";
import { splitReason, zeroFillTimeline } from "./radiusDeviceModel";
import { listRadiusServers } from "../../service/radiusService";

// RADIUS authentication log.
//
// Copied from components/authenticationPolicy/authEventLogs.js (the AD log page, left untouched)
// and narrowed to RADIUS: every query asks for protocol "radius", which is what authn-service
// writes to did.auth_log for each VPN / switch / firewall login it decides. The AD-only parts
// (Kerberos error names, "exclude intermediate / benign noise", the per-user drawer that mixes
// in AD events) are left out.
//
// Column mapping of a RADIUS row: gatewayId = NAS IP, serviceSpn = NAS-Identifier,
// clientIp = calling station, realm = AD domain, reason = "outcome · policy" (shown as two columns),
// radiusServerId = the FreeRADIUS server that sent the login (resolved to its name via
// /radius/ListServers; older bridges don't send it, shown as "Unknown server").
//
// Filters sent to /ad/GetAuthLog: radiusServerId (one server) and challenged: true ("MFA required
// only": logins that raised a push; their rows say allow or deny).

const { RangePicker } = DatePicker;

const FilterField = ({ label, children }) => (
  <div>
    <div
      style={{
        fontFamily: AN_MONO,
        fontSize: 10.5,
        letterSpacing: 1.1,
        textTransform: "uppercase",
        color: AN_COLORS.muted,
        marginBottom: 6,
        fontWeight: 500,
      }}
    >
      {label}
    </div>
    {children}
  </div>
);

const NoRowsOverlay = () => (
  <GridOverlay>
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 4 }}>
      <InboxOutlined style={{ fontSize: "24px", color: AN_COLORS.muted }} />
      <span>No RADIUS logins yet</span>
      <span style={{ fontSize: 12, color: AN_COLORS.muted }}>Logins refused by FreeRADIUS (wrong password) appear in its own log.</span>
    </div>
  </GridOverlay>
);

const DecisionTag = ({ decision }) => {
  const meta = decisionMeta(decision);
  return (
    <Tag
      style={{
        color: meta.color,
        background: `${anAlpha(meta.color, 8)}`,
        border: `1px solid ${anAlpha(meta.color, 20)}`,
        borderRadius: 6,
        fontWeight: 500,
        margin: 0,
      }}
    >
      {meta.label}
    </Tag>
  );
};

const Mono = ({ children, muted }) => (
  <span style={{ fontFamily: AN_MONO, color: muted ? AN_COLORS.muted : undefined }}>{children || "—"}</span>
);

const gridSx = {
  border: "none",
  fontFamily: AN_FONT,
  fontSize: 13,
  color: AN_COLORS.ink,
  "& .MuiDataGrid-columnHeaders": {
    background: AN_COLORS.bg,
    borderBottom: `1px solid ${AN_COLORS.line2}`,
    fontFamily: AN_MONO,
    fontSize: 10.5,
    letterSpacing: 1.1,
    textTransform: "uppercase",
    color: AN_COLORS.muted,
  },
  "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 500 },
  // Long values (reason, request id) wrap to a second line instead of being cut; two lines fit the 52px row.
  "& .MuiDataGrid-cell": {
    borderBottom: `1px solid ${AN_COLORS.line2}`,
    color: AN_COLORS.ink2,
    display: "flex",
    alignItems: "center",
    whiteSpace: "normal",
    lineHeight: 1.35,
  },
  "& .MuiDataGrid-row:hover": { background: AN_COLORS.bg },
  "& .MuiDataGrid-footerContainer": { borderTop: `1px solid ${AN_COLORS.line2}` },
};

// At most two lines in a cell; the full text is in the tooltip.
const twoLines = { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" };

const rowId = (e, i) => `${e.correlationId || "x"}-${e.decision || "d"}-${e.tsUnixMs || 0}-${i}`;

const cardStyle = {
  background: AN_COLORS.surface,
  border: `1px solid ${AN_COLORS.line}`,
  borderRadius: 10,
  padding: 16,
  marginBottom: 14,
};

export default function RadiusAuthLogs() {
  const [notificationApi, notificationContext] = message.useMessage();
  const gridScrollRef = useScrollbarWhileScrolling({ capture: true });

  const [userInput, setUserInput] = useState("");
  const debouncedUser = useDebounce(userInput, 500);
  const [decision, setDecision] = useState(undefined);
  const [range, setRange] = useState(null);
  const [serverId, setServerId] = useState(undefined);
  const [challengedOnly, setChallengedOnly] = useState(false);
  const [servers, setServers] = useState([]);

  const [events, setEvents] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 0, pageSize: 25 });

  const fetchLog = useCallback(() => {
    setIsLoading(true);
    const payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      protocol: "radius",
      page: pagination.page + 1, // API is 1-based
      pageSize: pagination.pageSize,
    };
    if (debouncedUser) payload.principal = debouncedUser.trim();
    if (decision) payload.decision = decision;
    if (range && range[0]) payload.sinceUnixMs = range[0].valueOf();
    if (range && range[1]) payload.untilUnixMs = range[1].valueOf();
    if (serverId) payload.radiusServerId = serverId;
    if (challengedOnly) payload.challenged = true;

    dispatchGetAuthLog({
      payload,
      onSuccess: (data) => {
        setIsLoading(false);
        setEvents(data?.events || []);
        setTimeline(data?.timeline || []);
        setTotal(data?.total || 0);
      },
      onFailure: ({ message: msg, isLogout }) => {
        setIsLoading(false);
        if (isLogout) {
          notificationApi.error("Session expired, please sign in again.");
          logOutUser();
          return;
        }
        notificationApi.error(msg || "Could not load RADIUS logins.");
      },
      onError: ({ message: msg }) => {
        setIsLoading(false);
        notificationApi.error(msg || "Could not load RADIUS logins.");
      },
    });
  }, [pagination, debouncedUser, decision, range, serverId, challengedOnly, notificationApi]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  // Back to the first page whenever a filter changes.
  useEffect(() => {
    setPagination((p) => (p.page === 0 ? p : { ...p, page: 0 }));
  }, [debouncedUser, decision, range, serverId, challengedOnly]);

  // RADIUS servers: names for the RADIUS server column and the filter. A failed load leaves
  // the column showing "Unknown server"; the log itself still loads.
  useEffect(() => {
    listRadiusServers({
      onSuccess: (data) => setServers(Array.isArray(data) ? data : []),
      onFailure: () => setServers([]),
    });
  }, []);
  const serverNames = useMemo(() => new Map(servers.map((s) => [s.id, s.name || s.sourceIp || s.id])), [servers]);

  const columns = useMemo(
    () => [
      {
        field: "tsUnixMs",
        headerName: "Time",
        flex: 1.1,
        minWidth: 165,
        // The request id (the old Request column) is the tooltip here: it is only needed to
        // match a row against the service logs, so it no longer takes a column.
        renderCell: ({ value, row }) => (
          <span title={row.correlationId ? `Request id: ${row.correlationId}` : undefined}>
            {value ? moment(value).format("MMM D, YYYY HH:mm:ss") : "—"}
          </span>
        ),
      },
      { field: "principal", headerName: "User", flex: 1, minWidth: 110 },
      {
        field: "decision",
        headerName: "Decision",
        flex: 0.7,
        minWidth: 85,
        renderCell: ({ value }) => <DecisionTag decision={value} />,
      },
      {
        field: "serviceSpn",
        headerName: "Device",
        flex: 1.1,
        minWidth: 130,
        renderCell: ({ row }) => (
          <div>
            <div>{row.serviceSpn || "—"}</div>
            {row.gatewayId ? (
              <div style={{ fontFamily: AN_MONO, fontSize: 12, color: AN_COLORS.muted }}>{row.gatewayId}</div>
            ) : null}
          </div>
        ),
      },
      {
        field: "clientIp",
        headerName: "Station",
        flex: 0.9,
        minWidth: 130,
        renderCell: ({ value }) => <Mono>{value}</Mono>,
      },
      {
        field: "reason",
        headerName: "Reason",
        flex: 1.5,
        minWidth: 180,
        // Plain text ("Push approved", "No answer within 60 s", ...), cut at two lines; the
        // whole reason as sent is the tooltip.
        renderCell: ({ value }) => {
          const [reason] = splitReason(value);
          return (
            <span title={value || undefined} style={twoLines}>
              {reason || "—"}
            </span>
          );
        },
      },
      {
        field: "policy",
        headerName: "Policy",
        flex: 1,
        minWidth: 130,
        sortable: false,
        valueGetter: (value, row) => splitReason(row.reason)[1],
        renderCell: ({ value }) => (
          <span title={value || undefined} style={twoLines}>
            {value || "—"}
          </span>
        ),
      },
      {
        field: "radiusServerId",
        headerName: "RADIUS server",
        flex: 1,
        minWidth: 130,
        sortable: false,
        renderCell: ({ value }) => {
          const name = value ? serverNames.get(value) : null;
          if (name) {
            return (
              <span title={name} style={twoLines}>
                {name}
              </span>
            );
          }
          // Older bridges send no server id; an id with no matching server (deleted) reads the same.
          return (
            <span title={value ? `Server id: ${value}` : undefined} style={{ color: AN_COLORS.muted }}>
              Unknown server
            </span>
          );
        },
      },
      { field: "realm", headerName: "Domain", flex: 0.8, minWidth: 100 },
      // Request column removed from the table; the id is the Time cell's tooltip.
    ],
    [serverNames]
  );

  const rows = useMemo(() => events.map((e, i) => ({ id: rowId(e, i), ...e })), [events]);
  const chartData = useMemo(() => bucketTimeline(zeroFillTimeline(timeline)), [timeline]);
  // The chart draws on a canvas, which cannot read the CSS variables.
  const { palette: chartPalette, mode: chartMode } = useAnTheme();
  const chartConfig = {
    data: chartData,
    xField: "label",
    yField: "count",
    height: 220,
    autoFit: true,
    // Same chart as the AD page, but straight lines (not "smooth"): RADIUS logins are few and
    // bursty, and smoothing draws hills over 15-minute buckets that had no logins.
    style: { fill: `l(270) 0:${chartPalette.primary}00 1:${chartPalette.primary}55` },
    line: { style: { stroke: chartPalette.primary, strokeWidth: 2 } },
    theme: chartMode === "dark" ? "classicDark" : "classic",
    axis: { x: { labelSpacing: 4 } },
    legend: false,
    tooltip: { title: "label", items: [{ field: "count", name: "Logins" }] },
  };

  return (
    <>
      {notificationContext}
      <div style={{ padding: "clamp(1rem, 3vw, 1.5rem) clamp(1rem, 3vw, 1.75rem)", fontFamily: AN_FONT, color: AN_COLORS.ink }}>
        <div style={{ marginBottom: 22 }}>
          <div
            style={{
              fontFamily: AN_MONO,
              fontSize: 10.5,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: AN_COLORS.muted,
              marginBottom: 6,
            }}
          >
            Admin · Radius · Logs
          </div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: -0.4, color: AN_COLORS.ink }}>
            Radius Authentication Logs
          </h1>
          <div style={{ fontSize: 13, color: AN_COLORS.muted, marginTop: 4 }}>
            {total.toLocaleString()} RADIUS logins: who connected, through which device, and what AuthNull decided.
          </div>
        </div>

        <div style={cardStyle}>
          <div
            style={{
              fontFamily: AN_MONO,
              fontSize: 10.5,
              letterSpacing: 1.1,
              textTransform: "uppercase",
              color: AN_COLORS.muted,
              marginBottom: 12,
              fontWeight: 500,
            }}
          >
            Login activity
          </div>
          {chartData.length ? (
            <Area {...chartConfig} />
          ) : (
            <div style={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center", color: AN_COLORS.muted, fontSize: 13 }}>
              No RADIUS logins for the selected filters.
            </div>
          )}
        </div>

        <div style={cardStyle}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(11.25rem, 1fr))",
              gap: 12,
              alignItems: "end",
            }}
          >
            <FilterField label="User">
              <Input
                allowClear
                placeholder="Search user"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                style={{ height: 38 }}
              />
            </FilterField>
            <FilterField label="Decision">
              <Select
                allowClear
                style={{ width: "100%", height: 38 }}
                placeholder="All decisions"
                value={decision}
                onChange={setDecision}
                options={[
                  { value: "allow", label: "Allow" },
                  { value: "deny", label: "Deny" },
                ]}
              />
            </FilterField>
            <FilterField label="Time range">
              <RangePicker showTime style={{ width: "100%", height: 38 }} value={range} onChange={setRange} />
            </FilterField>
            <FilterField label="RADIUS server">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ width: "100%", height: 38 }}
                placeholder="All servers"
                value={serverId}
                onChange={setServerId}
                options={servers.map((s) => ({ value: s.id, label: s.name || s.sourceIp || s.id }))}
                notFoundContent="No RADIUS servers"
              />
            </FilterField>
            <div style={{ height: 38, display: "flex", alignItems: "center" }}>
              <Checkbox checked={challengedOnly} onChange={(e) => setChallengedOnly(e.target.checked)}>
                MFA required only
              </Checkbox>
            </div>
          </div>
        </div>

        <div
          ref={gridScrollRef}
          className="an-grid-x"
          style={{
            background: AN_COLORS.surface,
            border: `1px solid ${AN_COLORS.line}`,
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            rowCount={total}
            loading={isLoading}
            getRowId={(row) => row.id}
            paginationMode="server"
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            disableColumnFilter
            autoHeight
            slots={{ noRowsOverlay: NoRowsOverlay, noResultsOverlay: NoRowsOverlay }}
            sx={gridSx}
          />
        </div>
      </div>
    </>
  );
}
